import type { CSSProperties, ReactNode } from 'react';

import { useTimeFormat } from '../lib/display.ts';
import { absoluteTime, observedTime, relativeShort, relativeTime } from '../lib/format.ts';
import { stateStyle, vendorStyle, type TicketState, type Vendor } from '../lib/palette.ts';

/* ── state chip ─────────────────────────────────────────────────────────────── */

export function StateChip({ state, size = 'sm' }: { state: TicketState; size?: 'sm' | 'md' }) {
  const style = stateStyle(state);
  return (
    <span
      className="chip"
      style={{
        background: style.wash,
        color: style.text,
        boxShadow: `inset 0 0 0 1px ${style.edge}`,
        fontSize: size === 'md' ? 'var(--fs-small)' : 'var(--fs-micro)',
        padding: size === 'md' ? '3px 9px' : '2px 7px',
      }}
    >
      {state}
    </span>
  );
}

export function StateDot({ state, pulse = false }: { state: TicketState; pulse?: boolean }) {
  const colour = stateStyle(state).line;
  return (
    <span
      className={`lamp${pulse ? ' lamp-breathe' : ''}`}
      style={{ background: colour, boxShadow: pulse ? `0 0 8px ${colour}` : undefined }}
      aria-hidden
    />
  );
}

/* ── vendor mark ────────────────────────────────────────────────────────────── */

/**
 * The vendor badge.
 *
 * First-class, never a tooltip: the reviewer is always the *opposite* vendor from the
 * implementer, and that adversarial pairing is the system's central design claim. If you
 * cannot tell at a glance which model wrote the code and which one criticised it, the most
 * interesting property of the pipeline is invisible.
 */
export function VendorMark({
  vendor,
  model,
  size = 'sm',
}: {
  vendor: Vendor;
  model?: string;
  size?: 'xs' | 'sm';
}) {
  const style = vendorStyle(vendor);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span
        className="chip"
        style={{
          background: style.wash,
          color: style.text,
          boxShadow: `inset 0 0 0 1px ${style.edge}`,
          fontSize: size === 'xs' ? 'var(--fs-micro)' : 'var(--fs-tiny)',
          padding: size === 'xs' ? '1px 5px' : '2px 6px',
          letterSpacing: '0.03em',
        }}
      >
        {style.label}
      </span>
      {model && model !== 'unknown' ? (
        <span className="mono" style={{ fontSize: 'var(--fs-tiny)', color: 'var(--text-4)' }}>
          {model}
        </span>
      ) : null}
    </span>
  );
}

/* ── time ───────────────────────────────────────────────────────────────────── */

/** Relative on the surface, absolute on hover — the brief's rule, in one component. */
export function Since({
  at,
  now,
  short = false,
  className,
  style,
}: {
  at: number;
  now: number;
  short?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const format = useTimeFormat();
  return (
    <span className={className} title={absoluteTime(at)} style={style}>
      {format === 'local'
        ? observedTime(at, now, 'local')
        : short
          ? relativeShort(at, now)
          : relativeTime(at, now)}
    </span>
  );
}

/* ── misc ───────────────────────────────────────────────────────────────────── */

export function Kicker({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span className="kicker">{children}</span>
      {count !== undefined ? (
        <span
          className="num"
          style={{
            fontSize: 'var(--fs-micro)',
            padding: '1px 6px',
            borderRadius: 4,
            background: 'rgba(207,214,228,0.07)',
            color: 'var(--text-3)',
          }}
        >
          {count}
        </span>
      ) : null}
    </span>
  );
}

export function Empty({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    /*
     * `empty-state` carries no styling. It is a hook: the screenshot harness has to be able to
     * assert that a view it drove into its empty or failed state actually reached it, and
     * before this there was nothing on the page to hold it to — so a shot of the wrong screen
     * and a shot of the right one were indistinguishable in the report.
     */
    <div
      className="empty-state"
      style={{
        display: 'grid',
        placeItems: 'center',
        height: '100%',
        padding: 'var(--s8)',
      }}
    >
      <div style={{ maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 'var(--s3)' }}>
        <h3 style={{ fontSize: 'var(--fs-large)' }}>{title}</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 'var(--fs-body)', lineHeight: 1.65 }}>{body}</p>
        {action}
      </div>
    </div>
  );
}

/**
 * A refresh failed and the last good read is still on screen.
 *
 * Views hold on to what they last read rather than blanking, which is right, but a failure
 * that only showed when there was nothing to hold on to left old numbers looking current.
 * `since` is when the data on screen was read; null when that is not known.
 */
export function RefreshError({
  what,
  error,
  since,
  now,
  onRetry,
}: {
  what: string;
  error: string;
  since: number | null;
  now: number;
  onRetry?: () => void;
}) {
  return (
    <div className="banner banner-warn refresh-error" role="status">
      <span className="dot" style={{ background: 'var(--warn)', width: 8, height: 8 }} />
      <strong style={{ fontWeight: 500 }}>{what} could not be refreshed.</strong>
      <span className="dim">
        {error}
        {since === null
          ? ' — what is shown is the last successful read.'
          : ` — what is shown was read ${relativeShort(since, now)} ago.`}
      </span>
      {onRetry ? (
        <button type="button" className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function Spinner({ size = 14 }: { size?: number }) {
  return (
    <span
      className="spinner"
      aria-hidden
      style={{
        width: size,
        height: size,
        display: 'inline-block',
        borderRadius: '50%',
        border: '1.6px solid rgba(207,214,228,0.2)',
        borderTopColor: 'var(--accent-bright)',
        animation: 'fm-spin 700ms linear infinite',
      }}
    />
  );
}
